'use client'

import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { EllipsisVertical, Trash2Icon } from 'lucide-react'
import { useState } from 'react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  UserAvatar,
} from '@/components/ui'
import { formatBytes } from '@/utils/formatBytes'

import { AttachmentViewerModal } from './attachmentViewerModal'
import { FileIcon } from './FileIcon'

import type { LocalAttachment } from './workflowAttachment'

type ItemAttachmentProps = {
  attachment: LocalAttachment
  onRemove: () => void
}

export function ItemAttachment({ attachment, onRemove }: ItemAttachmentProps) {
  const [isViewerOpen, setIsViewerOpen] = useState(false)

  const file = attachment.file

  const fileName = file?.name ?? attachment.originalName ?? 'Arquivo'
  const fileSize = file?.size ?? attachment.size ?? 0
  const fileType = file?.type ?? attachment.fileType ?? ''
  const createdAt = attachment.createdAt

  const isImage = fileType.startsWith('image/')

  const handleClick = () => {
    if (isImage && attachment.preview) {
      setIsViewerOpen(true)
      return
    }

    if (!attachment.preview && !attachment.s3Key) return

    // prioriza a URL completa (preview); s3Key costuma ser só o identificador
    const url = attachment.preview || attachment.s3Key || ''
    if (!url) return

    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <div className="grid grid-cols-[4fr_0.8fr_2fr_1.5fr_2rem] gap-4 px-4 py-3 transition-colors hover:bg-system-background-disabled">
        <div
          className="flex min-w-0 cursor-pointer items-center gap-2"
          onClick={handleClick}
        >
          <FileIcon mimeType={fileType} />
          <span className="truncate text-sm font-medium text-system-text-primary">
            {fileName}
          </span>
        </div>
        <div className="flex items-center truncate text-sm text-system-text-secondary">
          {formatBytes(fileSize)}
        </div>
        <div className="flex items-center truncate text-sm text-system-text-secondary">
          {createdAt
            ? format(new Date(createdAt), "d 'de' MMMM, yyyy", { locale: ptBR })
            : '-'}
        </div>
        <div className="flex items-center">
          <UserAvatar
            src={attachment.responsible?.image}
            name={attachment.responsible?.name}
            className="size-8"
          />
        </div>
        <div className="flex min-w-0 items-center justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-system-text-secondary hover:text-system-state-error"
              >
                <EllipsisVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onRemove}>
                <Trash2Icon className="size-4" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isImage && attachment.preview && (
        <AttachmentViewerModal
          attachment={{
            id: fileName,
            url: attachment.preview,
            originalName: fileName,
            fileType,
            size: fileSize,
            createdAt: new Date().toISOString(),
            fileName,
            ideaStepId: '',
            s3Key: attachment.s3Key || '',
            author: attachment.responsible ?? {
              id: '',
              name: '',
              image: null,
              area: null,
            },
          }}
          open={isViewerOpen}
          onOpenChange={setIsViewerOpen}
        />
      )}
    </>
  )
}
